<template>
  <div class="offer-sales-common-container" :class="bgAndBorder">
    <div v-if="limitedOffers.length === 0 && !previewMode" class="no-offers">
      <span>Nenhum produto foi encontrado com a sua busca.</span>
    </div>

    <div v-for="(offer, index) in limitedOffers" :key="index" :class="getOfferClasses(offer)">
      <div class="offer-sale-content">
        <div v-if="isProductsGroupPreview && isMobile" class="blue-chip mb-2" :class="getInactiveOfferSaleClass(offer)">Grupo de produtos</div>
        <div class="img-container">
          <img v-if="imageExists(offer)" :src="imageSource(offer)" alt="imagem do produto" width="100%" :class="getInactiveOfferSaleClass(offer)" />
          <mf-icon v-else size="36" :class="getInactiveOfferSaleClass(offer)">image_not_supported</mf-icon>
        </div>
        <div class="right-container">
          <div v-if="isProductsGroupPreview && !isMobile" class="blue-chip mb-2" :class="getInactiveOfferSaleClass(offer)">Grupo de produtos</div>
          <span class="product-name" :class="getInactiveOfferSaleClass(offer)">{{ offer.name | truncate({ length: 80 }) }}</span>

          <component
            :is="previewComponent"
            v-if="!isInsideProductsGroup && isMobile"
            class="mechanic-container"
            :class="getInactiveOfferSaleClass(offer)"
            :offer="offer"
            :preview-mode="previewMode"
          />
          <v-divider v-if="isMobile && !isInsideProductsGroup" />

          <div class="sku-ean-container" :class="getInactiveOfferSaleClass(offer)">
            <price-1-edit v-if="showPrice1Edit" :product="offer" :disabled="!offer.active" class="order-0" />
            <span v-if="!isInsideProductsGroup" class="format-rebate-text">
              <strong v-if="isCashback">Cashback (pago pela indústria): </strong>
              <strong v-else>Reembolso: </strong>
              <span>{{ calcPromotionText(offer) }}</span>
            </span>

            <span v-if="isProductsGroupPreview" class="cinza-0-14px"><strong>Produtos: </strong>{{ getProductsLength(offer) }}</span>
            <span v-else class="cinza-0-14px order-2"><strong>SKU: </strong>{{ offer.sku }}</span>

            <product-ean
              v-if="!isProductsGroupPreview"
              class="order-3"
              :offer="offer"
              :disabled="!offer.active || previewMode"
              :preview-mode="previewMode"
              :is-inside-products-group="isInsideProductsGroup"
            />
            <div class="d-flex flex-row align-center" style="gap: 16px">
              <span v-if="showMechanicText && offer.minimum_per_client" class="cinza-0-14px order-4">
                <strong>Mín. CPF: </strong>{{ offer.minimum_per_client }}
              </span>
              <span v-if="showMechanicText && offer.maximum_per_client" class="cinza-0-14px order-5">
                <strong>Máx. CPF: </strong>{{ offer.maximum_per_client }}
              </span>
              <span v-if="showMechanicText && isCashback && offer.max_coupons" class="cinza-0-14px order-5">
                <strong>Máx. ativação por CPF: </strong>{{ offer.max_coupons }}
              </span>
            </div>
          </div>
          <div v-if="!previewMode" class="status-container" :class="getToggleBackground(offer)">
            <mf-toggle
              id="industry_proposal_deactivate_product"
              v-model="offer.active"
              :label="offer.active ? 'Produto ativo na campanha' : 'Produto inativo na campanha'"
              hide-details
            />
          </div>
        </div>
      </div>
      <component
        :is="previewComponent"
        v-if="!isInsideProductsGroup && !isMobile"
        class="mechanic-container"
        :class="getInactiveOfferSaleClass(offer)"
        :offer="offer"
        :preview-mode="previewMode"
      />
    </div>
    <div v-if="previewMode" id="industry_proposal_view_more_products" class="see-more" @click="$emit('goToDetails')">
      <mf-icon v-if="remainingOffersLength > 0" color="marca-0-l10" size="18">add</mf-icon>
      <span>{{ getSeeMoreMessage }}</span>
    </div>
  </div>
</template>

<script>
import AbsoluteDiscountMechanic from '../products-mechanics/AbsoluteDiscountMechanic.vue'
import PercentageDiscountMechanic from '../products-mechanics/PercentageDiscountMechanic.vue'
import TakePayMechanic from '../products-mechanics/TakePayMechanic.vue'
import CashbackAbsoluteMechanic from '../products-mechanics/CashbackAbsoluteMechanic.vue'
import VoucherDigitalMechanic from '../products-mechanics/VoucherDigitalMechanic.vue'
import { localeString } from '@/config/helpers.js'
import Price1Edit from '../field-edit/Price1Edit.vue'
import ProductEan from '../field-edit/ProductEan.vue'

export default {
  name: 'OfferSalesCommon',
  components: {
    TakePayMechanic,
    AbsoluteDiscountMechanic,
    PercentageDiscountMechanic,
    CashbackAbsoluteMechanic,
    Price1Edit,
    ProductEan,
  },
  props: {
    offerSales: {
      type: Array,
      required: true,
    },
    type: {
      type: String,
      required: true,
      validator: (value) =>
        [
          'takepay',
          'takepay_offer_sales',
          'general',
          'offer_sales',
          'percentage',
          'percentage_offer_sales',
          'next_buy_cashback',
          'next_buy_cashback_offer_sales',
          'cashback_absolute',
          'cashback_absolute_offer_sales',
          'dynamic_rule_offer_sales',
        ].includes(value),
    },
    isProductsGroupPreview: {
      type: Boolean,
      default: false,
    },
    currentOfferTypeKeyName: {
      type: String,
      default: '',
    },
    isInsideProductsGroup: {
      type: Boolean,
      default: false,
    },
    previewMode: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    showOffersLimit() {
      return this.previewMode ? (this.isMobile ? 1 : 3) : this.offerSales.length
    },
    limitedOffers() {
      return this.offerSales.length > this.showOffersLimit ? this.offerSales.slice(0, this.showOffersLimit) : this.offerSales
    },
    previewComponent() {
      switch (this.type) {
        case 'takepay':
        case 'takepay_offer_sales':
          return TakePayMechanic
        case 'general':
        case 'offer_sales':
          return AbsoluteDiscountMechanic
        case 'percentage':
        case 'percentage_offer_sales':
          return PercentageDiscountMechanic
        case 'next_buy_cashback':
        case 'next_buy_cashback_offer_sales':
        case 'cashback_absolute':
        case 'cashback_absolute_offer_sales':
          return CashbackAbsoluteMechanic
        case 'dynamic_rule_offer_sales':
          return VoucherDigitalMechanic
        default:
          return null
      }
    },
    isCashback() {
      return (
        this.type === 'next_buy_cashback' ||
        this.type === 'cashback_absolute' ||
        this.type === 'cashback_absolute_offer_sales' ||
        this.type === 'next_buy_cashback_offer_sales'
      )
    },

    remainingOffersLength() {
      let remainingOffersLength = 0
      if (!this.isProductsGroupPreview) {
        remainingOffersLength = this.offerSales.length - this.showOffersLimit
      }
      return remainingOffersLength
    },

    getSeeMoreMessage() {
      if (this.remainingOffersLength > 0) {
        return `Ver mais ${this.remainingOffersLength} produto${this.remainingOffersLength > 1 ? 's' : ''}`
      } else return 'Ver mais detalhes'
    },

    bgAndBorder() {
      return this.previewMode ? 'bg-cinza-l90 preview-mode-border' : 'bg-white'
    },

    showPrice1Edit() {
      return (this.type === 'general' || this.type === 'offer_sales') && !this.previewMode && !this.isInsideProductsGroup
    },

    showMechanicText() {
      return this.isMobile && !this.isProductsGroupPreview && !this.isInsideProductsGroup
    },
  },
  methods: {
    calcPromotionText(offer) {
      if (this.type === 'takepay' || this.type === 'takepay_offer_sales') {
        const diff = offer?.take - offer?.pay
        if (diff === 1) return `${diff} unidade por oferta`
        return `${diff} unidades por oferta`
      } else if (this.type === 'general' || this.type === 'offer_sales') {
        return `R$ ${localeString(offer?.rebate, 2)}`
      } else if (this.type === 'percentage' || this.type === 'percentage_offer_sales') {
        return `Todo desconto concedido`
      } else if (this.isCashback) {
        return `R$ ${localeString(offer?.cashback_absolute_discount || offer?.cashback_absolute, 2)}`
      }
    },

    imageExists(offer) {
      if (this.isProductsGroupPreview) {
        return offer?.group_image_url && offer?.group_image_url?.length > 0
      } else {
        return offer?.image_url && offer?.image_url?.length > 0
      }
    },

    imageSource(offer) {
      if (this.isProductsGroupPreview) {
        return offer?.group_image_url
      } else {
        return offer?.image_url
      }
    },

    getInactiveOfferSaleClass(offer) {
      return !offer.active && !this.previewMode ? 'inactive-campaign' : ''
    },

    getOfferClasses(offer) {
      return [
        'offer-sale-item',
        {
          'normal-item-padding': !this.previewMode,
          'preview-mode-item-padding': this.previewMode,
          'outside-group-padding': !this.isInsideProductsGroup,
          'inactive-background': !offer.active && !this.previewMode,
        },
      ]
    },

    getToggleBackground(offer) {
      return offer.active ? 'bg-cinza-l90' : 'bg-white'
    },

    getProductsLength(offer) {
      return offer?.[this.currentOfferTypeKeyName]?.length || 0
    },
  },
}
</script>

<style lang="scss" scoped>
.offer-sales-common-container {
  border-radius: 8px;
  border-top-width: 0;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.preview-mode-border {
  border: 1px solid $cinza-l70;
}

.bg-cinza-l90 {
  background: $cinza-l90;
}

.inactive-background {
  background-color: #f5f6f7;
}

.bg-white {
  background: white;
}

.product-name,
.sku-ean-container,
.blue-chip,
.mechanic-container {
  transition: opacity 0.5s ease;
}

.inactive-campaign {
  opacity: 0.5;
}

.no-offers {
  display: flex;
  justify-content: center;
  padding: 52px;
  padding-bottom: 36px;
}

.offer-sale-item {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  transition: background-color 0.5s ease;
  border-bottom: 1px solid $cinza-l70;

  @media screen and (max-width: $xs-breakpoint) {
    border-width: 2px;
    justify-content: center;
  }
}

.normal-item-padding {
  padding: 20px 88px;

  @media screen and (max-width: $xs-breakpoint) {
    padding: 32px 16px;
  }
}

.preview-mode-item-padding {
  padding: 20px 56px 20px 76px;

  @media screen and (max-width: $xs-breakpoint) {
    padding: 32px 16px;
  }
}

.outside-group-padding {
  padding: 28px 40px 20px 40px;

  @media screen and (max-width: $xs-breakpoint) {
    padding: 32px 16px;
  }
}

.offer-sale-content {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 16px;

  @media screen and (max-width: $xs-breakpoint) {
    flex-direction: column;
  }

  .blue-chip {
    padding: 4px 16px;
    border-radius: 16px;
    font-size: 14px;
    font-weight: bold;
    background: #d2e9ff80;
    color: $marca-0-l10;
    white-space: nowrap;
    width: fit-content;
    user-select: none;
  }

  .product-name {
    font-size: 1rem;
    font-weight: bold;
    color: $cinza-d10;

    @media screen and (max-width: $xs-breakpoint) {
      text-align: center;
    }
  }

  .order-0 {
    order: 0;
  }

  .order-2 {
    order: 2;
  }

  .order-3 {
    order: 3;
  }

  .order-4 {
    order: 4;
  }

  .order-5 {
    order: 5;
  }

  .format-rebate-text {
    font-size: 14px;
    color: $verde-d20;
    order: 1;

    @media screen and (max-width: $xs-breakpoint) {
      order: 5;
    }
  }

  .cinza-0-14px {
    font-size: 14px;
    color: $cinza-0;
  }

  .right-container {
    display: flex;
    flex-direction: column;
    gap: 16px;

    @media screen and (max-width: $xs-breakpoint) {
      align-items: center;
    }

    .sku-ean-container {
      display: flex;
      flex-direction: row;
      align-items: center;
      flex-wrap: wrap;
      gap: 20px;

      @media screen and (max-width: $xs-breakpoint) {
        gap: 12px;
      }
    }

    .status-container {
      width: 542px;
      padding: 8px 12px;
      border-radius: 8px;
      transition: background-color 0.5s ease;

      @media screen and (max-width: $xs-breakpoint) {
        width: 100%;
      }
    }
  }
}

.img-container {
  width: 74px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;

  & * {
    transition: opacity 0.5s ease;
  }

  & img {
    display: block;
    max-width: 100%;
    max-height: 100%;
    mix-blend-mode: darken;
    object-fit: contain;
  }
}

.see-more {
  background: #d2e9ff99;
  color: $marca-0-l10;
  height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    cursor: pointer;
    background-color: #b4dbff99;
  }
}
</style>
